import _ from "lodash";

import HNSColors from "../../../../ui library/HNSColors";
import AddressFieldWithLabel from "../../../Common/AddressField/AddressFieldWithLabel";
// import SingleSelectDropdownFieldWithLabel from "../../../Common/Dropdowns/fixed-option-single-select-dropdown/SingleSelectDropdownWithLabel";
import SingleLineTextFieldWithLabel from "../../../Common/TextField/single-line-text-field/SingleLineTextFieldWithLabel";
import SingleSelectDropdown from "../components/fields/edit-only/SingleSelectDropdown";
import BooleanField from "../components/fields/read-only/BooleanField";
import ChipField from "../components/fields/read-only/ChipField";
// import ChipList from "../components/fields/read-only/ChipList";
import TextField from "../components/fields/read-only/TextField";
import NothingToShow from "../components/NothingToShow";
import { STYLES } from "../styles/style";

export function getInNetworkColor(inNetwork, receivingClients) {
  if (inNetwork && receivingClients === false) {
    return [HNSColors.warning[500], HNSColors.warning[400]];
  } else if (inNetwork) {
    return [HNSColors.success[700], HNSColors.success[400]];
  } else if (!inNetwork && receivingClients === false) {
    return [HNSColors.failure[500], HNSColors.failure[400]];
  } else if (!inNetwork) {
    return [HNSColors.failure[500], HNSColors.failure[400]];
  }
  return [HNSColors.neutrals[500], HNSColors.neutrals[400]];
}

export function getValueByPath(obj, path) {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

export function castValue(value, Type) {
  if (!Type || value === undefined || value === null) {
    return value;
  }

  switch (Type) {
    case Number:
      return Number(value);
    case String:
      return String(value);
    case Boolean:
      return value === "true" || value === true;
    default:
      return value;
  }
}

export const isEmptyValue = (value) => value === undefined || value === null || value === "";

export const resolveStaticLabel = (
  response,
  value,
  { modelValueKey, modelValueLabel, modelValueKeyType }
) => {
  if (!Array.isArray(response)) {
    return value;
  }
  const typedValue = castValue(value, modelValueKeyType);
  const matched = response.find((item) => item?.[modelValueKey] === typedValue);
  return matched?.[modelValueLabel] ?? "Unknown";
};

export function renderField(field = {}, data = {}) {
  const { displayStyle, modelKey } = field;
  const value = modelKey ? getValueByPath(data, modelKey) : undefined;

  switch (displayStyle) {
    case "text": {
      return <TextField value={value ?? ""} field={field} />;
    }

    case "chip": {
      return <ChipField value={value} field={field} />;
    }

    case "boolean": {
      return <BooleanField value={value} />;
    }

    default:
      return <NothingToShow />;
  }
}

export function renderEditField(field = {}, data = {}, customOnChange) {
  const { type, modelKey, label, name } = field;
  const value = modelKey ? getValueByPath(data, modelKey) : undefined;

  switch (type) {
    case "text":
      return (
        <SingleLineTextFieldWithLabel
          label={label}
          initialPosition="top"
          fieldName={name}
          fieldValue={value}
          customOnChange={customOnChange}
        />
      );

    case "address":
      return (
        <AddressFieldWithLabel
          label="Address"
          position="top"
          initialAddress={value}
          fieldName={name}
          fieldValue={value}
          customOnChange={customOnChange}
          defaultEditingMode={false}
        />
      );

    case "single-select-dropdown":
      return <SingleSelectDropdown field={field} data={data} customOnChange={customOnChange} />;

    default:
      return <NothingToShow />;
  }
}

export function getMenuList(menuOptions) {
  return {
    menuListItems: menuOptions.map((menuOption) => ({
      key: menuOption.key,
      name: menuOption.label,
      props: { sx: STYLES.menuItemStyle },
    })),
    props: {
      PaperProps: { sx: STYLES.menuListStyle },
      anchorOrigin: { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top", horizontal: "center" },
    },
  };
}

export function convertKeysToCamelCaseDeep(input) {
  if (_.isArray(input)) {
    return input.map(convertKeysToCamelCaseDeep);
  }

  if (_.isPlainObject(input)) {
    return _.transform(
      input,
      (result, value, key) => {
        const camelKey = _.camelCase(key);
        result[camelKey] = convertKeysToCamelCaseDeep(value);
      },
      {}
    );
  }

  return input;
}

export const isUnchanged = (initial, updated) => {
  return _.isEqual(initial, updated);
};

export const setNestedValue = (obj, path, value) => {
  _.set(obj, path, value);
  return obj;
};

export const deleteNestedValue = (obj, path) => {
  _.unset(obj, path);
  return obj;
};

export const getDeepDiff = (current, base) =>
  _.transform(current, (result, value, key) => {
    if (!_.isEqual(value, base[key])) {
      result[key] =
        _.isObject(value) && _.isObject(base[key]) ? getDeepDiff(value, base[key]) : value;
    }
  });

export const buildRawAddress = (address) => {
  const secondary =
    address.suiteType && address.suiteNumber ? ` ${address.suiteType} ${address.suiteNumber}` : "";

  return `${address.streetNumber} ${address.route}${secondary}, ${address.city} ${address.state} ${address.postCode}`;
};

export const updateAddress = async (address, addressRepository) => {
  try {
    const raw = buildRawAddress(address);
    await addressRepository.patch(address.id, {
      ...address,
      raw,
      formatted: raw,
    });
  } catch (err) {
    console.error(err);
    return Promise.reject(err);
  }
};

export const splitCollectionDiff = (modified, initial, mapUpdate, mapCreate) => {
  const update = [];
  const create = [];
  const remove = [];

  Object.entries(modified).forEach(([key, value]) => {
    if (initial[key]) {
      update.push(mapUpdate(initial[key], value));
    } else {
      create.push(mapCreate(key, value));
    }
  });

  Object.entries(initial).forEach(([key, value]) => {
    if (!modified[key]) {
      remove.push(value.id);
    }
  });

  return { update, create, remove };
};
